import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Menu, X } from "lucide-react";

const links = [
  ["Conceito", "#conceito"],
  ["Projeto", "#projeto"],
  ["Localização", "#localizacao"],
  ["Áreas comuns", "#areas"],
  ["Apartamentos", "#apartamentos"],
  ["Rentabilidade", "#investimento"],
  ["Sanches + Housi", "#housi"],
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const toggleRef = useRef(null);
  const mobileNavRef = useRef(null);
  const brandRef = useRef(null);
  const destinationRef = useRef(null);

  function closeMenu(href) {
    // AnimatePresence keeps the leaving panel mounted briefly. Exclude those
    // departing links from the tab order as soon as the menu closes.
    if (mobileNavRef.current) mobileNavRef.current.inert = true;
    destinationRef.current = href || null;
    setOpen(false);
    if (!href) toggleRef.current?.focus({ preventScroll: true });
  }

  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 40);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("menu-open", open);
    if (!open) {
      const href = destinationRef.current;
      destinationRef.current = null;
      if (!href) return;
      const destination = document.getElementById(href.slice(1));
      if (!destination) return;
      const frame = window.requestAnimationFrame(() => {
        // Let the anchor retain native hash/history/scroll behavior, while
        // putting keyboard users at the selected content instead of the header.
        const hadTabIndex = destination.hasAttribute("tabindex");
        if (!hadTabIndex) destination.setAttribute("tabindex", "-1");
        destination.focus({ preventScroll: true });
        if (!hadTabIndex) destination.addEventListener("blur", () => destination.removeAttribute("tabindex"), { once: true });
      });
      return () => window.cancelAnimationFrame(frame);
    }

    if (mobileNavRef.current) mobileNavRef.current.inert = false;
    const frame = window.requestAnimationFrame(() => mobileNavRef.current?.querySelector("a[href]")?.focus({ preventScroll: true }));
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (mobileNavRef.current) mobileNavRef.current.inert = true;
        setOpen(false);
        toggleRef.current?.focus({ preventScroll: true });
        return;
      }
      if (event.key !== "Tab") return;
      const items = [toggleRef.current, ...Array.from(mobileNavRef.current?.querySelectorAll("a[href], button:not([disabled])") || [])].filter(Boolean);
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      if (!items.includes(current) || (!event.shiftKey && current === last) || (event.shiftKey && current === first)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus({ preventScroll: true });
      }
    };
    const onResize = () => {
      if (window.innerWidth > 1024) {
        const focusWasInMenu = mobileNavRef.current?.contains(document.activeElement) || document.activeElement === toggleRef.current;
        if (mobileNavRef.current) mobileNavRef.current.inert = true;
        setOpen(false);
        // The mobile toggle becomes hidden on desktop; the brand stays visible.
        if (focusWasInMenu) brandRef.current?.focus({ preventScroll: true });
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      window.cancelAnimationFrame(frame);
      document.body.classList.remove("menu-open");
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  return (
    <header className={`site-header ${scrolled ? "is-scrolled" : ""}`}>
      <a ref={brandRef} className="brand" href="#inicio" aria-label="La Courbe Tower — início">
        <img src="/assets/sanches-logo.png" alt="Sanches Incorporadora e Construtora" />
        <span>LA COURBE TOWER</span>
      </a>
      <nav className="desktop-nav" aria-label="Navegação principal">
        {links.map(([label, href]) => <a href={href} key={href}>{label}</a>)}
      </nav>
      <a className="button button-small" href="#contato">Saiba mais</a>
      <button
        ref={toggleRef}
        className="menu-toggle"
        type="button"
        aria-expanded={open}
        aria-controls="mobile-menu"
        aria-label={open ? "Fechar menu" : "Abrir menu"}
        onClick={() => open ? closeMenu() : setOpen(true)}
      >
        {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
      </button>
      <AnimatePresence>
        {open ? (
          <motion.nav
            ref={mobileNavRef}
            className="mobile-nav react-mobile-nav"
            id="mobile-menu"
            aria-label="Navegação móvel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
          >
            {links.map(([label, href]) => (
              <a href={href} key={href} onClick={() => closeMenu(href)}>{label}</a>
            ))}
            <a className="button" href="#contato" onClick={() => closeMenu("#contato")}>Quero saber mais</a>
          </motion.nav>
        ) : null}
      </AnimatePresence>
    </header>
  );
}
